import type { AxiosError } from 'axios'
import { isRequestAbort } from '~/requestAbort'

/** One extra try, half a second later. Used by the read calls we retry. */
export const NO_RESPONSE_RETRY_MS = 500

function wait(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * The browser got no HTTP answer. A call we stopped ourselves is not this.
 */
export function isNoHttpResponse(error: unknown): boolean {
  if (isRequestAbort(error)) {
    return false
  }
  if (!error || typeof error !== 'object') {
    return false
  }
  const err = error as AxiosError
  if (err.response) {
    return false
  }
  return (
    err.isAxiosError === true ||
    err.code === 'ERR_NETWORK' ||
    err.message === 'Network Error'
  )
}

export async function retryOnNoResponse<T>(
  run: () => Promise<T>,
  options: {
    extraAttempts: number
    delayMs: (attempt: number) => number
    shouldRetry?: (error: unknown) => boolean
    signal?: AbortSignal
  }
): Promise<T> {
  const shouldRetry = options.shouldRetry ?? isNoHttpResponse
  let attempt = 0
  while (true) {
    try {
      return await run()
    } catch (error) {
      const canRetry =
        attempt < options.extraAttempts &&
        !options.signal?.aborted &&
        shouldRetry(error)
      if (!canRetry) {
        throw error
      }
      await wait(options.delayMs(attempt))
      if (options.signal?.aborted) {
        throw error
      }
      attempt += 1
    }
  }
}

/** Stable path for Sentry. Container numbers and ids must not split one issue. */
export function endpointTemplate(url: string | undefined): string {
  if (!url) {
    return 'unknown'
  }
  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0]
  return path
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '{id}'
    )
    .replace(/[A-Z]{4}\d{7}/gi, '{container}')
    .replace(/\/\d+(?=\/|$)/g, '/{id}')
}

export function networkErrorGrouping(error: unknown): {
  fingerprint: string[]
  tags: Record<string, string>
} | null {
  if (!isNoHttpResponse(error)) {
    return null
  }
  const err = error as AxiosError
  const method = (err.config?.method || 'GET').toUpperCase()
  const endpoint = endpointTemplate(err.config?.url)
  const code = err.code || 'ERR_NETWORK'
  const online =
    typeof navigator === 'undefined' ? 'true' : String(navigator.onLine)
  return {
    fingerprint: ['network-error', method, endpoint, code],
    tags: {
      'api.endpoint': endpoint,
      'http.method': method,
      'navigator.onLine': online,
    },
  }
}
